<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :stacked="formModal || nestedModal"
        :padding="false"
        size="lg">
        <template v-slot:header>
            <div class="px-6 pt-6">
                Snippets
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6 pb-4">
                Reusable snippets of content and variables to speed up your workflow.
            </div>

            <div v-if="FEATURES.contact_groups && !isEffectivelyCorporate && !props.onlyLockedSnippetsAllowed" class="pb-4 px-6">
                <vx-tabs v-model="currentTab" size="lg">
                    <vx-tab value="nonCorporate">{{ effectiveContext.orgUnitId && !effectiveContext.groupId ? 'Organizational Unit' : 'Group' }}</vx-tab>
                    <vx-tab value="corporate" class="gap-1.5"><font-awesome-icon widthAuto size="lg" :icon="faCrown"></font-awesome-icon> Corporate</vx-tab>
                </vx-tabs>
            </div>

            <div class="px-6 flex flex-col md:flex-row gap-2">
                <div v-if="'nonCorporate' === currentTab && !props.onlyLockedSnippetsAllowed" class="flex-1">
                    <vx-input
                        v-model="searchSnippets"
                        size="md"
                        placeholder="Search"
                    >
                        <template v-slot:left>
                            <font-awesome-icon widthAuto class="text-slate-800 mr-1" :icon="faMagnifyingGlass"></font-awesome-icon>
                        </template>
                    </vx-input>
                </div>
                <div v-else class="flex-1">
                    <vx-input
                        v-model="searchTemplates"
                        size="md"
                        placeholder="Search"
                    >
                        <template v-slot:left>
                            <font-awesome-icon widthAuto class="text-slate-800 mr-1" :icon="faMagnifyingGlass"></font-awesome-icon>
                        </template>
                    </vx-input>
                </div>

                <vx-button v-if="'nonCorporate' === currentTab && !props.onlyLockedSnippetsAllowed"
                    data-test="new-snippet-btn"
                    class="w-auto"
                    @click.prevent="formModal = true"
                    type="button"
                    size="md"
                    color="muted">
                    New <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>

        <div class="pt-2 px-6 pb-6">
            <div v-if="!list?.length && loading"
                class="py-36 text-center">
                <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
            </div>
            <template v-else-if="!list?.length && !loading">
                <div class="pt-4 text-center">
                    No results found
                </div>
            </template>
            <div v-else class="flex flex-col gap-4">
                <message-builder-snippet-card v-for="snippet in list"
                    v-model:nestedModal="nestedModal"
                    :modelValue="snippet"
                    :blockFiles="props.blockFiles"
                    :blockImages="props.blockImages"
                    :key="'snippet-' + snippet.id"
                    class="bg-white [&>div]:p-0"
                    @selected="select(snippet)"
                ></message-builder-snippet-card>
                <div v-if="fetchParams.cursor" class="mt-8">
                    <vx-button
                        @click="getSnippets()"
                        color="muted-dark"
                        size="lg"
                        class="grow-0"
                        data-test="load-more-btn"
                        :loading="loading"
                    >
                        Load More
                        <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>
    </vx-modal>

    <message-builder-new-snippet
        v-model:visible="formModal"
        @created="select"
        :disableMedia="props.disableMedia"
        :blockImages="props.blockImages"
        :blockFiles="props.blockFiles"
        :contextOrgUnitId="effectiveContext.orgUnitId"
        :contextGroupId="effectiveContext.groupId"
    ></message-builder-new-snippet>
</template>

<script setup>
import { faCirclePlus, faCrown, faMagnifyingGlass, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxInput, VxModal, VxIconLoading, VxTabs, VxTab } from '@voxie/frontend-components';
import debounce from '~/utils/debounce';
import { computed, inject, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { snippetsClient } from '~/services';
import MessageBuilderNewSnippet from './MessageBuilderNewSnippet.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import MessageBuilderSnippetCard from '~/components/general/message-builder/modals/MessageBuilderSnippetCard.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';

const toasted = useToasted();
const FEATURES = inject('FEATURES');
const { getCurrentGroupId, currentOrgUnit } = useCurrentGroup();

const props = defineProps({
    visible: Boolean,
    disableMedia: Boolean,
    blockImages: {
        type: Boolean,
        default: false,
    },
    blockFiles: {
        type: Boolean,
        default: false,
    },
    onlyLockedSnippetsAllowed: {
        type: Boolean,
        default: false,
    },
    disableLockedSnippets: {
        type: Boolean,
        default: true,
    },
    contextOrgUnitId: {
        type: Number,
        default: undefined,
    },
    contextGroupId: {
        type: String,
        default: undefined,
    },
});
const emit = defineEmits(['update:visible', 'selected']);

const nestedModal = ref(false);
const formModal = ref(false);
const teamId = Spark.state.currentTeam.id;

const hasExplicitContext = computed(() => props.contextOrgUnitId !== undefined || props.contextGroupId !== undefined);

const effectiveContext = computed(() => {
    if (hasExplicitContext.value) {
        if (props.contextGroupId) {
            return { teamId, groupId: props.contextGroupId };
        }

        if (props.contextOrgUnitId) {
            return { teamId, orgUnitId: props.contextOrgUnitId };
        }

        return { teamId };
    }

    return { teamId, orgUnitId: currentOrgUnit?.value?.id, groupId: getCurrentGroupId?.() };
});

const isEffectivelyCorporate = computed(() => !effectiveContext.value.orgUnitId && !effectiveContext.value.groupId);

const currentTab = ref('nonCorporate');

const searchSnippets = ref('');
const searchTemplates = ref('');

const search = computed(() => {
    if (currentTab.value === 'corporate') {
        return searchTemplates.value;
    }

    return searchSnippets.value;
});

const snippets = ref([]);
const templates = ref([]);

const list = computed(() => {
    if (currentTab.value === 'corporate' || props.onlyLockedSnippetsAllowed) {
        return templates.value;
    }

    return snippets.value;
});

const templatesRequested = ref(false);

const select = (snippet) => {
    emit('selected', snippet);
    emit('update:visible', false);
}

const backToDefaultState = () => {
    searchSnippets.value = '';
    searchTemplates.value = '';
    templatesRequested.value = false;
    fetchParams.value = defaultFilters();
    snippets.value = [];
    templates.value = [];
    records.value = [];
    currentTab.value = 'nonCorporate';
};

const { list: records, fetchParams, loading, fetchRecords } = useCursorPagination();

const defaultFilters = () => {
    return {
        per_page: 15,
        query: '',
        sort_field: 'title',
        sort_dir: 'asc',
        cursor: null,
    };
};

const getSnippets = async () => {
    const params = { ...fetchParams.value, query: search.value };

    params['filter[is_template]'] = ('corporate' === currentTab.value || props.onlyLockedSnippetsAllowed) ? 1 : 0;

    // do not send is_locked if we need to get all snippets
    if (props.disableLockedSnippets) {
        params['filter[is_locked]'] = 0;
    } else if (props.onlyLockedSnippetsAllowed) {
        params['filter[is_locked]'] = 1;
    }

    try {
        await fetchRecords(snippetsClient.getSnippets(effectiveContext.value, params));

        if (1 === params['filter[is_template]']) {
            templates.value = records.value;
            templatesRequested.value = true;
        } else {
            snippets.value = records.value;
        }
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    }
};

watch([searchSnippets, searchTemplates, () => props.visible], debounce(() => {
    if (!props.visible) {
        backToDefaultState();
        return;
    }

    fetchParams.value = defaultFilters();
    getSnippets();
}, 300), { immediate: true })

watch(currentTab, (newCurrentTab) => {
    if (newCurrentTab === 'corporate' && templatesRequested.value === false) {
        fetchParams.value = defaultFilters();
        getSnippets();
    }
});

</script>
